import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './Transition.module.css';
import Overview from './Overview';
import Tracker from './Tracker';
import Wizard from './Wizard';
import {
    AddRowDialog,
    ColumnsDialog,
    CompletedDialog,
    ConflictDialog,
    FailedDialog,
    HowImportingDialog,
    PasteDialog,
    VersionConflictDialog,
    ChooseHeaderRowDialog,
    SheetPreviewDialog,
    resetColumns,
} from './Dialogs';
import { UploadIcon } from './icons';
import { ATTENTION_REASONS, DEFAULT_COLUMNS, PAGE_SIZE, SORT_KEYS, STATUS_OPTIONS, emptyAddForm } from './constants';
import { showsFileStatus, sourceLine, toTrackerItem } from './records';
import {
    bulkUpdate,
    cancelImport,
    commitImport,
    createImport,
    createRecord,
    deleteImportFile,
    exportRecords,
    getImport,
    getPreferences,
    getRecord,
    getFilters,
    getSummary,
    listImports,
    listRecords,
    listViews,
    pasteCommit,
    pastePreview,
    patchRecord,
    previewImport,
    saveImportDecisions,
    saveImportDraft,
    saveImportMappings,
    suggestImportAiMapping,
    savePreferences,
    isUploadParseError,
    transitionErrorMessage,
    uploadImportFile,
    getImportWorksheet,
} from '../../apiServices/transitionApi';

const initialWizard = {
    step: 1,
    updateChoice: 'add',
    sourceId: '',
    batchId: null,
    files: [],
    matchSheetId: null,
    reviewed: false,
    preview: null,
    result: null,
    statusRules: {},
    statusSamples: [],
    dateSamples: [],
    dateConvention: '',
    error: '',
    busy: false,
    aiMappingMessage: '',
};

export default function Transition({ projectId: projectIdProp }) {
    const navigate = useNavigate();
    const selectedProject = useSelector(state => state?.projectData?.selectedProject);
    const projectId = projectIdProp || selectedProject?.id || (typeof localStorage === 'undefined' ? '' : localStorage.getItem('project_id'));
    const [tab, setTab] = React.useState('overview');
    const [summary, setSummary] = React.useState(null);
    const [items, setItems] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [views, setViews] = React.useState([{ key: 'all', label: 'All items' }]);
    const [viewKey, setViewKey] = React.useState('all');
    const [viewLabel, setViewLabel] = React.useState('All items');
    const [search, setSearch] = React.useState('');
    const [query, setQuery] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState(null);
    const [functionFilter, setFunctionFilter] = React.useState(null);
    const [ownerFilter, setOwnerFilter] = React.useState(null);
    const [migrationFilter, setMigrationFilter] = React.useState(null);
    const [dateFilter, setDateFilter] = React.useState(null);
    const [filterOptions, setFilterOptions] = React.useState(null);
    const [pasteWorkstreamId, setPasteWorkstreamId] = React.useState('');
    const [versionConflict, setVersionConflict] = React.useState(null);
    const [page, setPage] = React.useState(1);
    const [sort, setSort] = React.useState({ key: null, dir: 'asc' });
    const [selected, setSelected] = React.useState([]);
    const [columns, setColumns] = React.useState(DEFAULT_COLUMNS);
    const [columnDraft, setColumnDraft] = React.useState(DEFAULT_COLUMNS);
    const [menu, setMenu] = React.useState(null);
    const [dialog, setDialog] = React.useState(null);
    const [detail, setDetail] = React.useState(null);
    const [addForm, setAddForm] = React.useState(emptyAddForm());
    const [pasteStep, setPasteStep] = React.useState('edit');
    const [pasteText, setPasteText] = React.useState('');
    const [pasteHeaders, setPasteHeaders] = React.useState(true);
    const [pastePreviewData, setPastePreviewData] = React.useState(null);
    const [pasteDuplicates, setPasteDuplicates] = React.useState([]);
    const [pasteError, setPasteError] = React.useState('');
    const [pasteBusy, setPasteBusy] = React.useState(false);
    const [wizard, setWizard] = React.useState(null);
    const [sheetDialog, setSheetDialog] = React.useState(null);
    const [history, setHistory] = React.useState([]);
    const [importResult, setImportResult] = React.useState(null);
    const [conflictRow, setConflictRow] = React.useState(null);
    const [failureMessage, setFailureMessage] = React.useState('');
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [reloadToken, setReloadToken] = React.useState(0);
    const batchRef = React.useRef(null);
    const wizardRef = React.useRef(wizard);
    React.useEffect(() => {
        wizardRef.current = wizard;
    }, [wizard]);

    const reload = React.useCallback(() => setReloadToken(value => value + 1), []);
    const hasRecords = (summary?.totals?.tracked_items || 0) > 0;
    const capabilities = summary?.capabilities || {};
    const canWrite = capabilities.can_write !== false;
    const aiMappingEnabled = capabilities.ai_mapping_enabled === true;
    const aiMappingAuto = capabilities.ai_mapping_auto !== false;
    const canImport = capabilities.can_import !== false;
    const resumeDraft = capabilities.resume_draft;
    const workstreams = capabilities.workstreams || [];
    const filterCount = [statusFilter, functionFilter, ownerFilter, migrationFilter, dateFilter].filter(Boolean).length;

    const clearFilters = () => {
        setStatusFilter(null);
        setFunctionFilter(null);
        setOwnerFilter(null);
        setMigrationFilter(null);
        setDateFilter(null);
        setPage(1);
        setMenu(null);
    };

    React.useEffect(() => {
        const handle = window.setTimeout(() => setQuery(search), 250);
        return () => window.clearTimeout(handle);
    }, [search]);

    React.useEffect(() => {
        const onKey = event => {
            if (event.key === 'Escape') {
                setDialog(null);
                setMenu(null);
                setDetail(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    React.useEffect(() => {
        if (!projectId) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        const params = {
            page,
            page_size: PAGE_SIZE,
            view_key: viewKey || 'all',
        };
        if (query) params.q = query;
        if (sort.key) {
            params.sort = SORT_KEYS[sort.key] || 'title';
            params.direction = sort.dir;
        }
        if (statusFilter === 'attention') params.attention = ATTENTION_REASONS.join(',');
        else if (statusFilter) params.status = statusFilter;
        if (functionFilter) params.business_function = functionFilter;
        if (ownerFilter) params.owner = ownerFilter;
        if (migrationFilter) params.migration_type = migrationFilter;
        if (dateFilter) params.target_date = dateFilter;
        Promise.all([
            getSummary(projectId),
            listRecords(projectId, params),
            listViews(projectId),
            getPreferences(projectId, viewKey),
            getFilters(projectId),
        ])
            .then(([summaryResponse, recordResponse, viewResponse, preferenceResponse, filtersResponse]) => {
                if (cancelled) return;
                setSummary(summaryResponse.data);
                setItems((recordResponse.data.results || []).map(toTrackerItem));
                setTotal(recordResponse.data.count || 0);
                const nextViews = viewResponse.data.views || [];
                setViews(nextViews.length ? nextViews : [{ key: 'all', label: 'All items' }]);
                setFilterOptions(filtersResponse?.data || null);
                const visible = preferenceResponse.data.visible_columns || [];
                if (visible.length) {
                    const next = { ...DEFAULT_COLUMNS, item: true };
                    Object.keys(next).forEach(key => {
                        next[key] = key === 'item' || visible.includes(key);
                    });
                    setColumns(next);
                }
            })
            .catch(loadError => {
                if (!cancelled) setError(transitionErrorMessage(loadError));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId, page, query, viewKey, statusFilter, functionFilter, ownerFilter, migrationFilter, dateFilter, sort, reloadToken]);

    const openTracker = (nextStatus, businessFunction) => {
        setTab('tracker');
        setStatusFilter(nextStatus || null);
        setFunctionFilter(businessFunction || null);
        setPage(1);
        setMenu(null);
    };

    const openDetail = async id => {
        setMenu(null);
        try {
            const response = await getRecord(projectId, id);
            setDetail(toTrackerItem(response.data));
        } catch (detailError) {
            setError(transitionErrorMessage(detailError));
        }
    };

    const changeItem = async (id, changes) => {
        const current = items.find(item => item.id === id) || (detail?.id === id ? detail : null);
        if (!current) return;
        const body = { expected_version: current.version };
        if (changes.title !== undefined) {
            const title = changes.title.trim();
            if (!title) {
                setError('Item / Description is required.');
                return;
            }
            body.title = title;
        }
        if (changes.status) {
            body.status_canonical = STATUS_OPTIONS.find(option => option.label === changes.status)?.value || changes.status;
        }
        if (changes.migrationType) body.migration_type_raw = changes.migrationType;
        if (Object.prototype.hasOwnProperty.call(changes, 'targetDate')) body.target_date = changes.targetDate || null;
        if (changes.owner) body.owner_raw = changes.owner;
        try {
            await patchRecord(projectId, id, body);
            reload();
            if (detail?.id === id) openDetail(id);
        } catch (editError) {
            const code = editError?.response?.data?.code;
            if (code === 'VERSION_CONFLICT') {
                setVersionConflict({ id, title: current.title });
                return;
            }
            setError(transitionErrorMessage(editError));
        }
    };

    const applyBulk = async changes => {
        const payload = {};
        if (changes.status) payload.status_canonical = STATUS_OPTIONS.find(option => option.label === changes.status)?.value || changes.status;
        if (changes.owner) payload.owner_raw = changes.owner;
        if (changes.targetDate) payload.target_date = changes.targetDate;
        if (changes.migrationType) payload.migration_type_raw = changes.migrationType;
        try {
            await bulkUpdate(projectId, { ids: selected, changes: payload, confirm: true });
            setSelected([]);
            reload();
        } catch (bulkError) {
            setError(transitionErrorMessage(bulkError));
        }
    };

    const downloadExport = async ids => {
        try {
            const response = await exportRecords(projectId, {
                scope: ids ? 'selected' : 'current_view',
                ids,
                format: 'csv',
                query: {
                    q: query,
                    view_key: viewKey,
                    status: statusFilter && statusFilter !== 'attention' ? statusFilter : undefined,
                    attention: statusFilter === 'attention' ? ATTENTION_REASONS.join(',') : undefined,
                    business_function: functionFilter || undefined,
                    owner: ownerFilter || undefined,
                    migration_type: migrationFilter || undefined,
                    target_date: dateFilter || undefined,
                },
            });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'transition-tracker.csv';
            link.click();
            URL.revokeObjectURL(url);
        } catch (exportError) {
            setError(transitionErrorMessage(exportError));
        }
    };

    const openHistory = async result => {
        try {
            const response = await listImports(projectId);
            setHistory((response.data.results || []).filter(batch => batch.status !== 'CANCELLED'));
        } catch (historyError) {
            setError(transitionErrorMessage(historyError));
        }
        setImportResult(result || null);
        setDialog('completed');
    };

    const openWizard = async () => {
        setDialog(null);
        let sources = [];
        try {
            const response = await listImports(projectId);
            const seen = new Set();
            sources = (response.data.results || [])
                .filter(batch => batch.status === 'COMMITTED' && batch.source_id && !seen.has(batch.source_id) && seen.add(batch.source_id))
                .map(batch => ({
                    id: batch.source_id,
                    label: (batch.filenames || []).filter(Boolean).join(', ') || `Import ${batch.id}`,
                }));
        } catch (sourceError) {
            setError(transitionErrorMessage(sourceError));
        }
        batchRef.current = null;
        setWizard({ ...initialWizard, updateChoice: hasRecords ? 'update' : 'add', sourceId: sources[0]?.id || '', sources });
    };

    const patchWizard = partial => setWizard(current => (current ? { ...current, ...partial } : current));

    const ensureBatch = async current => {
        if (batchRef.current) return batchRef.current;
        const snapshot = current || wizardRef.current || {};
        const mode = hasRecords ? snapshot.updateChoice || 'update' : 'first';
        const body = { mode };
        if (mode === 'update') {
            if (!snapshot.sourceId) {
                throw new Error('Choose which existing source you are updating before uploading.');
            }
            body.source_id = Number(snapshot.sourceId);
        }
        const created = await createImport(projectId, body);
        batchRef.current = created.data.id;
        patchWizard({ batchId: created.data.id });
        return created.data.id;
    };

    const waitForImportParse = async batchId => {
        const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
        for (let attempt = 0; attempt < 45; attempt += 1) {
            const detail = await getImport(projectId, batchId);
            const files = detail.data.files || [];
            patchWizard({ batchId, files, busy: true });
            const pending = files.some(file => {
                const status = String(file.parse_status || 'pending').toLowerCase();
                return status === 'pending' || status === 'processing';
            });
            if (!pending || !files.length) return detail;
            await delay(1500);
        }
        return getImport(projectId, batchId);
    };

    const uploadFile = async file => {
        patchWizard({ busy: true, error: '' });
        try {
            const batchId = await ensureBatch(wizardRef.current);
            const uploaded = await uploadImportFile(projectId, batchId, file);
            const asyncParse = String(uploaded.data?.processing || '').toLowerCase() === 'async';
            let detail = await getImport(projectId, batchId);
            const needsPoll =
                asyncParse ||
                (detail.data.files || []).some(file => {
                    const status = String(file.parse_status || 'pending').toLowerCase();
                    return status === 'pending' || status === 'processing';
                });
            if (needsPoll) {
                detail = await waitForImportParse(batchId);
            }
            const files = detail.data.files || [uploaded.data];
            const stillPending = files.some(file => {
                const status = String(file.parse_status || 'pending').toLowerCase();
                return status === 'pending' || status === 'processing';
            });
            const failed = files.filter(file => String(file.parse_status || '').toLowerCase() === 'failed');
            patchWizard({
                busy: false,
                batchId,
                files,
                matchSheetId: files.find(f => (f.worksheets || []).length)?.worksheets?.[0]?.id || null,
                error: stillPending
                    ? 'This file is still being parsed. If Continue stays disabled, start the Celery worker (transition parse tasks) or try again in a moment.'
                    : failed.length
                      ? failed.map(f => f.parse_error || 'This file could not be read.').join(' ')
                      : '',
            });
        } catch (uploadError) {
            const message = uploadError?.message?.includes('existing source')
                ? uploadError.message
                : transitionErrorMessage(uploadError);
            const batchId = batchRef.current;
            if (batchId) {
                try {
                    const detail = await getImport(projectId, batchId);
                    patchWizard({
                        busy: false,
                        batchId,
                        files: detail.data.files || [],
                        error: message,
                    });
                } catch {
                    patchWizard({ busy: false, error: message });
                }
            } else {
                patchWizard({ busy: false, error: message });
            }
            const keepInWizard =
                uploadError?.message?.includes('existing source') || isUploadParseError(uploadError);
            if (!keepInWizard) {
                setFailureMessage(message);
                setDialog('failed');
            }
        }
    };

    const removeFile = async snapshotId => {
        if (!wizard?.batchId) return;
        try {
            await deleteImportFile(projectId, wizard.batchId, snapshotId);
            const detail = await getImport(projectId, wizard.batchId);
            patchWizard({ files: detail.data.files || [] });
        } catch (removeError) {
            patchWizard({ error: transitionErrorMessage(removeError) });
        }
    };

    const updateSheet = (sheetId, changes) => {
        setWizard(current =>
            current
                ? {
                      ...current,
                      files: current.files.map(file => ({
                          ...file,
                          worksheets: (file.worksheets || []).map(sheet => (sheet.id === sheetId ? { ...sheet, ...changes } : sheet)),
                      })),
                  }
                : current,
        );
    };

    const mappingPayload = current => {
        const worksheets = {};
        const columns = {};
        (current.files || []).forEach(file => {
            (file.worksheets || []).forEach(sheet => {
                worksheets[sheet.id] = {
                    selected: Boolean(sheet.selected),
                    kind: sheet.kind || 'other',
                    kind_confirmed: true,
                    header_confirmed: Boolean(sheet.header_confirmed) || !sheet.needs_header_review,
                    header_row_index: sheet.header_row_index ?? 0,
                };
                (sheet.columns || []).forEach(column => {
                    const target = column.target || column.suggestion?.proposed_field || 'custom';
                    let confirmed = target !== 'custom';
                    if (target === 'target_date') confirmed = Boolean(current.dateConvention);
                    columns[`${sheet.id}:${column.index}`] = { target, confirmed };
                });
            });
        });
        return {
            worksheets,
            columns,
            status_rules: current.statusRules || {},
            date_rules: current.dateConvention ? { convention: current.dateConvention, confirmed: true } : {},
        };
    };

    const runImportAiMapping = async ({ force = false, worksheetIds = null } = {}) => {
        if (!wizard?.batchId || !aiMappingEnabled) return null;
        patchWizard({ busy: true, error: '', aiMappingMessage: '' });
        try {
            const body = { force };
            if (worksheetIds?.length) body.worksheet_ids = worksheetIds;
            const response = await suggestImportAiMapping(projectId, wizard.batchId, body);
            const ai = response.data.ai_mapping || {};
            const message = ai.results?.some(item => item.ok)
                ? 'AI updated column suggestions — please verify before continuing.'
                : '';
            patchWizard({
                busy: false,
                files: response.data.files || wizard.files,
                aiMappingMessage: message,
            });
            return response.data;
        } catch (aiError) {
            patchWizard({ busy: false, aiMappingMessage: '', error: transitionErrorMessage(aiError) });
            return null;
        }
    };

    const goToStep = async next => {
        if (!wizard) return;
        patchWizard({ busy: true, error: '' });
        try {
            if (wizard.step === 2 && next === 3) {
                const { worksheets, status_rules, date_rules } = mappingPayload(wizard);
                await saveImportMappings(projectId, wizard.batchId, {
                    worksheets,
                    columns: {},
                    status_rules: wizard.statusRules || status_rules,
                    date_rules: wizard.dateConvention ? { convention: wizard.dateConvention, confirmed: true } : date_rules,
                });
                let detail = await getImport(projectId, wizard.batchId);
                if (aiMappingEnabled && aiMappingAuto) {
                    await runImportAiMapping();
                    detail = await getImport(projectId, wizard.batchId);
                }
                const statusSamples = detail.data.status_samples || [];
                const dateSamples = detail.data.date_samples || [];
                patchWizard({
                    busy: false,
                    step: 3,
                    files: detail.data.files || wizard.files,
                    statusSamples,
                    dateSamples,
                    statusRules: mergeStatusRules(wizard.statusRules, statusSamples),
                    matchSheetId:
                        wizard.matchSheetId ||
                        detail.data.files?.[0]?.worksheets?.find(sheet => sheet.selected)?.id ||
                        detail.data.files?.[0]?.worksheets?.[0]?.id,
                });
                return;
            }
            if (wizard.step === 3 && next === 4) {
                await saveImportMappings(projectId, wizard.batchId, mappingPayload(wizard));
                const preview = await previewImport(projectId, wizard.batchId);
                patchWizard({ busy: false, step: 4, preview: preview.data, reviewed: false });
                return;
            }
            if (next < wizard.step) {
                patchWizard({ busy: false, step: next, reviewed: false });
                return;
            }
            patchWizard({ busy: false, step: next });
        } catch (stepError) {
            patchWizard({ busy: false, error: transitionErrorMessage(stepError) });
        }
    };

    const commitWizard = async () => {
        patchWizard({ busy: true, error: '' });
        try {
            const detail = await getImport(projectId, wizard.batchId);
            const committed = await commitImport(projectId, wizard.batchId, {
                confirmation: true,
                review_plan_hash: wizard.preview?.review_plan_hash || detail.data.review_plan_hash,
                idempotency_key: window.crypto?.randomUUID ? window.crypto.randomUUID() : `import-${Date.now()}`,
            });
            let payload = committed.data;
            if (payload.status === 'COMMITTING') {
                patchWizard({
                    busy: true,
                    commitMessage: 'Import is running in the background. Large workbooks can take a few minutes.',
                });
                let finished = false;
                for (let attempt = 0; attempt < 90; attempt += 1) {
                    await new Promise(resolve => window.setTimeout(resolve, 2000));
                    const latest = await getImport(projectId, wizard.batchId);
                    if (latest.data.status === 'COMMITTED') {
                        payload = { ...latest.data, result: latest.data.summary || {} };
                        finished = true;
                        break;
                    }
                    if (latest.data.status === 'FAILED') {
                        throw new Error(latest.data.error_message || 'Import failed.');
                    }
                    if (latest.data.status === 'CANCELLED') {
                        throw new Error('This import was cancelled before it finished.');
                    }
                }
                if (!finished && payload.status === 'COMMITTING') {
                    throw new Error(
                        'Import is still running. Refresh Transition or open Import history in a minute to see the result.',
                    );
                }
            }
            patchWizard({ busy: false, step: 5, result: payload });
            setImportResult(payload);
            reload();
        } catch (commitError) {
            patchWizard({ busy: false, error: transitionErrorMessage(commitError) });
        }
    };

    const saveDraft = async () => {
        if (!wizard?.batchId || wizard.step >= 5) {
            batchRef.current = null;
            setWizard(null);
            reload();
            return;
        }
        try {
            await saveImportDraft(projectId, wizard.batchId, { ...mappingPayload(wizard), step: wizard.step });
            batchRef.current = null;
            setWizard(null);
            reload();
        } catch (draftError) {
            patchWizard({ error: transitionErrorMessage(draftError) });
        }
    };

    const closeWizard = () => {
        batchRef.current = null;
        setWizard(null);
        reload();
    };

    const resumeImportDraft = async () => {
        const draft = resumeDraft || summary?.capabilities?.resume_draft;
        if (!draft?.batch_id) return;
        setDialog(null);
        try {
            const detail = await getImport(projectId, draft.batch_id);
            batchRef.current = draft.batch_id;
            const files = detail.data.files || [];
            const draftState = detail.data.draft_state || {};
            setWizard({
                ...initialWizard,
                step: Math.min(Math.max(detail.data.step || draft.step || 1, 1), 5),
                batchId: draft.batch_id,
                files,
                matchSheetId: files.flatMap(file => file.worksheets || [])[0]?.id || null,
                statusRules: draftState.status_rules || {},
                updateChoice: detail.data.mode === 'update' ? 'update' : 'add',
                sourceId: detail.data.source_id ? String(detail.data.source_id) : '',
            });
        } catch (resumeError) {
            setError(transitionErrorMessage(resumeError));
        }
    };

    const submitAdd = async () => {
        const title = addForm.title.trim();
        if (!title) return;
        try {
            await createRecord(projectId, {
                title,
                description: title,
                business_function_raw: addForm.businessFunction,
                owner_raw: addForm.owner,
                status_canonical: addForm.status,
                migration_type_raw: addForm.migrationType,
                target_date: addForm.targetDate || undefined,
                client_request_id: window.crypto?.randomUUID ? window.crypto.randomUUID() : `row-${Date.now()}`,
            });
            setDialog(null);
            setTab('tracker');
            reload();
        } catch (addError) {
            setError(transitionErrorMessage(addError));
        }
    };

    const previewPaste = async () => {
        setPasteBusy(true);
        setPasteError('');
        try {
            const response = await pastePreview(projectId, { text: pasteText, headers: pasteHeaders });
            const titles = (response.data.rows || []).filter(row => row.check === 'ready' && row.title).map(row => row.title.trim());
            const lookups = await Promise.all(titles.map(title => listRecords(projectId, { q: title, page_size: 20 })));
            const duplicates = titles.filter((title, index) =>
                (lookups[index].data.results || []).some(row => (row.title || '').trim().toLowerCase() === title.toLowerCase()),
            );
            setPasteDuplicates([...new Set(duplicates)]);
            setPastePreviewData(response.data);
            setPasteStep('preview');
        } catch (previewError) {
            setPasteError(transitionErrorMessage(previewError));
        } finally {
            setPasteBusy(false);
        }
    };

    const confirmPaste = async () => {
        setPasteBusy(true);
        setPasteError('');
        try {
            const selectedWorkstream = workstreams.find(item => String(item.id) === String(pasteWorkstreamId));
            await pasteCommit(projectId, {
                text: pasteText,
                headers: pasteHeaders,
                workstream_id: pasteWorkstreamId ? Number(pasteWorkstreamId) : undefined,
                business_function_raw: selectedWorkstream?.name || undefined,
            });
            setDialog(null);
            setPasteStep('edit');
            setTab('tracker');
            reload();
        } catch (commitError) {
            setPasteError(transitionErrorMessage(commitError));
        } finally {
            setPasteBusy(false);
        }
    };

    const applyColumns = async () => {
        setColumns(columnDraft);
        setDialog(null);
        const visible = Object.entries(columnDraft).filter(([, shown]) => shown).map(([key]) => key);
        try {
            await savePreferences(projectId, { view_key: viewKey || 'all', visible_columns: visible, sort });
        } catch (preferenceError) {
            setError(transitionErrorMessage(preferenceError));
        }
    };

    const findSheet = sheetId => (wizard?.files || []).flatMap(file => file.worksheets || []).find(sheet => sheet.id === sheetId);

    const resolveWorksheet = (files, ref) => {
        const sheets = (files || []).flatMap(file =>
            (file.worksheets || []).map(sheet => ({ ...sheet, filename: file.filename, snapshotId: file.id })),
        );
        if (!ref) return null;
        let sheet = sheets.find(item => item.id === ref.id);
        if (!sheet && ref.name) {
            sheet = sheets.find(item => item.name === ref.name && (!ref.filename || item.filename === ref.filename));
        }
        return sheet || null;
    };

    const syncImportDetail = async currentWizard => {
        const detail = await getImport(projectId, currentWizard.batchId);
        const files = detail.data.files || [];
        return {
            files,
            statusSamples: detail.data.status_samples || [],
            dateSamples: detail.data.date_samples || [],
            statusRules: mergeStatusRules(currentWizard.statusRules, detail.data.status_samples),
        };
    };

    const mergeStatusRules = (currentRules, samples) => {
        const rules = { ...(currentRules || {}) };
        (samples || []).forEach(sample => {
            if (rules[sample.raw]) return;
            const canonical = sample.suggestion?.canonical || 'UNKNOWN';
            rules[sample.raw] = {
                canonical,
                confirmed: Boolean(canonical && canonical !== 'UNKNOWN'),
            };
        });
        return rules;
    };

    const reloadImportFiles = async () => {
        if (!wizard?.batchId) return;
        const synced = await syncImportDetail(wizard);
        patchWizard(synced);
    };

    const openSheetPreview = async sheetId => {
        if (!wizard?.batchId) return;
        const ref = findSheet(sheetId);
        setSheetDialog({ mode: 'preview', sheetId, sheetName: ref?.name, loading: true, error: '', data: null });
        try {
            const synced = await syncImportDetail(wizard);
            patchWizard(synced);
            const sheet = resolveWorksheet(synced.files, { id: sheetId, name: ref?.name, filename: ref?.filename });
            if (!sheet) {
                setSheetDialog({ mode: 'preview', sheetId, loading: false, error: 'That worksheet was refreshed. Close and open preview again.', data: null });
                return;
            }
            const response = await getImportWorksheet(projectId, wizard.batchId, sheet.id);
            setSheetDialog({ mode: 'preview', sheetId: sheet.id, loading: false, error: '', data: response.data });
        } catch (previewError) {
            setSheetDialog({ mode: 'preview', sheetId, loading: false, error: transitionErrorMessage(previewError), data: null });
        }
    };

    const openHeaderPicker = async sheetId => {
        if (!wizard?.batchId) return;
        const ref = findSheet(sheetId);
        setSheetDialog({ mode: 'header', sheetId, sheetName: ref?.name, loading: true, error: '', data: null, busy: false });
        try {
            const synced = await syncImportDetail(wizard);
            patchWizard(synced);
            const sheet = resolveWorksheet(synced.files, { id: sheetId, name: ref?.name, filename: ref?.filename });
            if (!sheet) {
                setSheetDialog({
                    mode: 'header',
                    sheetId,
                    loading: false,
                    error: 'That worksheet was refreshed. Close this dialog and click Choose header row again.',
                    data: null,
                    busy: false,
                });
                return;
            }
            const response = await getImportWorksheet(projectId, wizard.batchId, sheet.id);
            setSheetDialog({ mode: 'header', sheetId: sheet.id, loading: false, error: '', data: response.data, busy: false });
        } catch (headerError) {
            setSheetDialog({ mode: 'header', sheetId, loading: false, error: transitionErrorMessage(headerError), data: null, busy: false });
        }
    };

    const applyHeaderRow = async headerRowIndex => {
        if (!wizard?.batchId || !sheetDialog?.sheetId) return;
        const ref = findSheet(sheetDialog.sheetId);
        setSheetDialog(current => (current ? { ...current, busy: true, error: '' } : current));
        try {
            const synced = await syncImportDetail(wizard);
            const sheet = resolveWorksheet(synced.files, { id: sheetDialog.sheetId, name: ref?.name, filename: ref?.filename });
            if (!sheet) {
                patchWizard(synced);
                setSheetDialog(current =>
                    current ? { ...current, busy: false, error: 'That worksheet was refreshed. Close and try again.' } : current,
                );
                return;
            }
            const worksheets = {
                ...mappingPayload({ ...wizard, files: synced.files }).worksheets,
                [sheet.id]: {
                    selected: true,
                    kind: sheet.kind || 'other',
                    kind_confirmed: true,
                    header_confirmed: true,
                    header_row_index: headerRowIndex,
                },
            };
            await saveImportMappings(projectId, wizard.batchId, { ...mappingPayload({ ...wizard, files: synced.files }), worksheets });
            const after = await syncImportDetail(wizard);
            const nextMatch =
                after.files.flatMap(file => file.worksheets || []).find(item => item.name === sheet.name)?.id || wizard.matchSheetId;
            patchWizard({ ...after, matchSheetId: nextMatch });
            setSheetDialog(null);
        } catch (applyError) {
            try {
                const after = await syncImportDetail(wizard);
                patchWizard(after);
            } catch (syncError) {
                patchWizard({ error: transitionErrorMessage(syncError) });
            }
            setSheetDialog(current => (current ? { ...current, busy: false, error: transitionErrorMessage(applyError) } : current));
        }
    };

    const chooseField = async (fieldId, choice) => {
        if (!wizard?.batchId) return;
        try {
            await saveImportDecisions(projectId, wizard.batchId, { fields: [{ id: fieldId, choice }] });
            const preview = await previewImport(projectId, wizard.batchId);
            patchWizard({ preview: preview.data, reviewed: false });
            setConflictRow(preview.data.rows?.find(row => row.fields?.some(field => field.id === fieldId)) || null);
        } catch (choiceError) {
            setError(transitionErrorMessage(choiceError));
        }
    };

    if (!projectId) {
        return <div className={styles.page}>Open a project before using Transition.</div>;
    }

    return (
        <div className={styles.pageRoot}>
            {error ? <p className={styles.warn}>{error}</p> : null}
            {wizard ? (
                <Wizard
                    wizard={{ ...wizard, hasRecords }}
                    aiMappingEnabled={aiMappingEnabled}
                    onAiMapping={() => runImportAiMapping({ force: true, worksheetIds: wizard.matchSheetId ? [wizard.matchSheetId] : null })}
                    onClose={closeWizard}
                    onSaveDraft={saveDraft}
                    onFile={uploadFile}
                    onRemoveFile={removeFile}
                    onStep={goToStep}
                    onToggleSheet={sheetId => updateSheet(sheetId, { selected: !wizard.files.flatMap(file => file.worksheets || []).find(sheet => sheet.id === sheetId)?.selected })}
                    onPurpose={(sheetId, kind) => updateSheet(sheetId, { kind })}
                    onMatchSheet={matchSheetId => patchWizard({ matchSheetId })}
                    onMapColumn={(sheetId, index, target) =>
                        updateSheet(sheetId, {
                            columns: (wizard.files.flatMap(file => file.worksheets || []).find(sheet => sheet.id === sheetId)?.columns || []).map(column =>
                                column.index === index ? { ...column, target } : column,
                            ),
                        })
                    }
                    onStatusRule={(raw, canonical) =>
                        patchWizard({
                            statusRules: {
                                ...wizard.statusRules,
                                [raw]: { canonical: canonical || 'UNKNOWN', confirmed: Boolean(canonical) },
                            },
                        })
                    }
                    onDateConvention={dateConvention => patchWizard({ dateConvention })}
                    onReviewed={reviewed => patchWizard({ reviewed })}
                    onImport={commitWizard}
                    onOpenOverview={() => {
                        setWizard(null);
                        setTab('overview');
                        reload();
                    }}
                    onOpenTracker={() => {
                        setWizard(null);
                        setTab('tracker');
                        reload();
                    }}
                    onViewDetails={() => openHistory(wizard.result)}
                    onUpdateMode={(updateChoice, sourceId) => patchWizard({ updateChoice, sourceId: sourceId === undefined ? wizard.sourceId : sourceId })}
                    onReviewRow={row => {
                        setConflictRow(row);
                        setDialog('conflict');
                    }}
                    onPreviewSheet={openSheetPreview}
                    onChooseHeaderRow={openHeaderPicker}
                />
            ) : (
                <div className={styles.page}>
                    <div className={styles.headerRow}>
                        <div>
                            <div className={styles.crumbs}>
                                <button type="button" className={styles.crumbBtn} onClick={() => navigate('/overview')}>
                                    Project Overview
                                </button>
                                <span>/</span>
                                <span>Transition</span>
                            </div>
                            <h1 className={styles.title}>Transition</h1>
                            <p className={styles.eyebrow}>Merge · Realise & Go Live</p>
                        </div>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={openWizard}
                            disabled={!canImport}
                            title={canImport ? undefined : 'You do not have permission to import into Transition.'}>
                            <UploadIcon /> Import / Update
                        </button>
                    </div>
                    <div className={styles.tabs} role="tablist" aria-label="Transition views">
                        <button type="button" className={tab === 'overview' ? styles.tabActive : styles.tab} onClick={() => setTab('overview')}>
                            overview
                        </button>
                        <button type="button" className={tab === 'tracker' ? styles.tabActive : styles.tab} onClick={() => setTab('tracker')}>
                            tracker
                        </button>
                    </div>
                    {loading ? <p className={styles.finePrint}>Loading Transition…</p> : null}
                    {resumeDraft && !wizard ? (
                        <div className={styles.draftBanner}>
                            <span>
                                You have a saved import draft
                                {resumeDraft.filenames?.length ? ` (${resumeDraft.filenames.join(', ')})` : ''} · step {resumeDraft.step || 1} of 5
                            </span>
                            <button type="button" className={styles.primaryBtn} onClick={resumeImportDraft} disabled={!canImport}>
                                Resume import
                            </button>
                        </div>
                    ) : null}
                    {tab === 'overview' && !loading && !hasRecords ? (
                        <div className={styles.emptyWrap}>
                            <section className={styles.emptyCard}>
                                <h2 className={styles.emptyTitle}>Bring your existing tracker into Lens</h2>
                                <p className={styles.emptyCopy}>
                                    Already managing your transition in Excel? Import it and pick up where you left off. Lens will help organise the sheets, match the columns and create your overview.
                                </p>
                                <div className={styles.emptyActions}>
                                    <button type="button" className={styles.primaryBtn} onClick={openWizard} disabled={!canImport}>
                                        <UploadIcon /> Import spreadsheet
                                    </button>
                                    <button type="button" className={styles.linkBtn} onClick={() => setTab('tracker')}>
                                        Start with a blank tracker
                                    </button>
                                    <button type="button" className={styles.mutedBtn} onClick={() => setDialog('how')}>
                                        How importing works
                                    </button>
                                </div>
                                <p className={styles.emptyNote}>Your original file is preserved. Review all proposed changes before they are applied.</p>
                            </section>
                        </div>
                    ) : null}
                    {tab === 'overview' && hasRecords ? (
                        <Overview summary={summary} onOpenItem={openDetail} onOpenTracker={openTracker} onImport={openWizard} onHistory={() => openHistory(null)} />
                    ) : null}
                    {tab === 'tracker' ? (
                        <Tracker
                            rows={items}
                            totalCount={total}
                            hasRecords={hasRecords}
                            loading={loading}
                            search={search}
                            view={viewKey}
                            viewLabel={viewLabel}
                            views={views}
                            statusFilter={statusFilter}
                            functionFilter={functionFilter}
                            ownerFilter={ownerFilter}
                            migrationFilter={migrationFilter}
                            dateFilter={dateFilter}
                            filterOptions={filterOptions}
                            filterCount={filterCount}
                            canWrite={canWrite}
                            columns={columns}
                            selected={selected}
                            inlineEdit
                            page={page}
                            sort={sort}
                            menu={menu}
                            pageSize={PAGE_SIZE}
                            onSearch={value => {
                                setSearch(value);
                                setPage(1);
                            }}
                            onView={(key, label) => {
                                setViewKey(key);
                                setViewLabel(label || key);
                                setPage(1);
                                setMenu(null);
                            }}
                            onStatusFilter={value => {
                                setStatusFilter(value);
                                setPage(1);
                                setMenu(null);
                            }}
                            onFunctionFilter={value => {
                                setFunctionFilter(value);
                                setPage(1);
                                setMenu(null);
                            }}
                            onOwnerFilter={value => {
                                setOwnerFilter(value);
                                setPage(1);
                                setMenu(null);
                            }}
                            onMigrationFilter={value => {
                                setMigrationFilter(value);
                                setPage(1);
                                setMenu(null);
                            }}
                            onDateFilter={value => {
                                setDateFilter(value);
                                setPage(1);
                                setMenu(null);
                            }}
                            onClearFilters={clearFilters}
                            onToggleMenu={next => setMenu(current => (current === next ? null : next))}
                            onCloseMenu={() => setMenu(null)}
                            onToggleRow={id => setSelected(current => (current.includes(id) ? current.filter(itemId => itemId !== id) : [...current, id]))}
                            onTogglePage={ids =>
                                setSelected(current => {
                                    const allSelected = ids.every(id => current.includes(id));
                                    return allSelected ? current.filter(id => !ids.includes(id)) : Array.from(new Set([...current, ...ids]));
                                })
                            }
                            onSort={key => {
                                setPage(1);
                                setSort(current => ({ key, dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc' }));
                            }}
                            onPage={setPage}
                            onOpen={openDetail}
                            onChangeItem={changeItem}
                            onAdd={() => {
                                if (!canWrite) return;
                                setAddForm(emptyAddForm());
                                setDialog('add');
                            }}
                            onImport={canImport ? openWizard : undefined}
                            onPaste={() => {
                                if (!canWrite) return;
                                setPasteStep('edit');
                                setPasteError('');
                                setPasteWorkstreamId('');
                                setDialog('paste');
                            }}
                            onColumns={() => {
                                setColumnDraft(columns);
                                setDialog('columns');
                            }}
                            onExport={ids => downloadExport(Array.isArray(ids) ? ids : null)}
                            onToggleEdit={() => setMenu(null)}
                            onBulkApply={applyBulk}
                        />
                    ) : null}
                </div>
            )}
            {sheetDialog?.mode === 'preview' ? (
                <SheetPreviewDialog sheet={sheetDialog.data} loading={sheetDialog.loading} error={sheetDialog.error} onClose={() => setSheetDialog(null)} />
            ) : null}
            {sheetDialog?.mode === 'header' ? (
                <ChooseHeaderRowDialog
                    sheet={sheetDialog.data}
                    loading={sheetDialog.loading}
                    error={sheetDialog.error}
                    busy={sheetDialog.busy}
                    onClose={() => setSheetDialog(null)}
                    onApply={applyHeaderRow}
                />
            ) : null}
            {dialog === 'how' ? <HowImportingDialog onClose={() => setDialog(null)} onStart={openWizard} /> : null}
            {dialog === 'completed' ? (
                <CompletedDialog result={importResult} history={history} onClose={() => setDialog(null)} onOverview={() => { setDialog(null); setWizard(null); setTab('overview'); }} onTracker={() => { setDialog(null); setWizard(null); setTab('tracker'); }} />
            ) : null}
            {dialog === 'conflict' ? <ConflictDialog row={conflictRow} onClose={() => setDialog(null)} onChoice={chooseField} /> : null}
            {versionConflict ? (
                <VersionConflictDialog
                    title={versionConflict.title}
                    onClose={() => setVersionConflict(null)}
                    onReload={() => {
                        const { id } = versionConflict;
                        setVersionConflict(null);
                        reload();
                        openDetail(id);
                    }}
                />
            ) : null}
            {dialog === 'failed' ? (
                <FailedDialog message={failureMessage} onClose={() => setDialog(null)} onRetry={() => setDialog(null)} onReview={() => setDialog(null)} onBack={() => { setDialog(null); setWizard(null); }} />
            ) : null}
            {dialog === 'add' ? (
                <AddRowDialog form={addForm} onChange={changes => setAddForm(current => ({ ...current, ...changes }))} onClose={() => setDialog(null)} onSubmit={submitAdd} />
            ) : null}
            {dialog === 'columns' ? (
                <ColumnsDialog
                    draft={columnDraft}
                    onToggle={id => setColumnDraft(current => ({ ...current, [id]: id === 'item' ? true : !current[id] }))}
                    onReset={() => setColumnDraft(resetColumns())}
                    onApply={applyColumns}
                    onClose={() => setDialog(null)}
                />
            ) : null}
            {dialog === 'paste' ? (
                <PasteDialog
                    step={pasteStep}
                    text={pasteText}
                    headers={pasteHeaders}
                    preview={pastePreviewData}
                    duplicates={pasteDuplicates}
                    error={pasteError}
                    busy={pasteBusy}
                    workstreams={workstreams}
                    workstreamId={pasteWorkstreamId}
                    onWorkstream={setPasteWorkstreamId}
                    onText={setPasteText}
                    onHeaders={setPasteHeaders}
                    onClose={() => setDialog(null)}
                    onPreview={previewPaste}
                    onBack={() => setPasteStep(current => (current === 'confirm' ? 'preview' : 'edit'))}
                    onReview={() => setPasteStep('confirm')}
                    onConfirm={confirmPaste}
                />
            ) : null}
            {detail ? <Detail item={detail} onClose={() => setDetail(null)} onSave={changes => changeItem(detail.id, changes)} /> : null}
        </div>
    );
}

function Detail({ item, onClose, onSave }) {
    const [sourceOpen, setSourceOpen] = React.useState(true);
    const [customOpen, setCustomOpen] = React.useState(false);
    const [title, setTitle] = React.useState(item.title);
    React.useEffect(() => setTitle(item.title), [item.title, item.version]);
    const statusText = showsFileStatus(item) ? `${item.statusRaw} (not confirmed)` : item.status || '—';
    const fields = [
        ['Business Function', item.businessFunction || '—'],
        ['Migration Type', item.migrationType || '—'],
        ['Owner', item.owner || '—'],
        ['Status', statusText],
        ['Target Date', item.targetDate || '—'],
        ['Attention', item.attention || '—'],
    ];
    const customEntries =
        (item.customFieldEntries || []).length > 0
            ? item.customFieldEntries
            : Object.entries(item.customFields || {}).map(([key, value]) => ({ key, label: key, value }));
    return (
        <div className={styles.drawerScrim} onMouseDown={onClose}>
            <aside className={styles.drawer} role="dialog" aria-label={item.title} onMouseDown={event => event.stopPropagation()}>
                <div className={styles.drawerHead}>
                    <div className={styles.drawerTitleBlock}>
                        <div className={styles.drawerTitleEditor}>
                            <label className={`${styles.field} ${styles.drawerTitleField}`}>
                                <span className={styles.fieldLabel}>Item / Description</span>
                                <input className={styles.input} aria-label="Edit title" value={title} onChange={event => setTitle(event.target.value)} />
                            </label>
                            <button
                                type="button"
                                className={styles.ghostBtn}
                                disabled={!title.trim() || title.trim() === item.title}
                                onClick={() => onSave({ title })}>
                                Save title
                            </button>
                        </div>
                        <p className={styles.drawerCode}>
                            {item.code || 'No code'} · {item.source}
                        </p>
                    </div>
                    <button type="button" className={styles.linkBtn} onClick={onClose}>
                        Close
                    </button>
                </div>
                <div className={styles.drawerBody}>
                    {item.description ? <p className={styles.detailCopy}>{item.description}</p> : null}
                    <div className={styles.detailGrid}>
                        {fields.map(([label, value]) => (
                            <div key={label}>
                                <p className={styles.detailLabel}>{label}</p>
                                <p className={styles.detailValue}>{value}</p>
                            </div>
                        ))}
                    </div>
                    <button type="button" className={styles.disclosure} onClick={() => setSourceOpen(open => !open)}>
                        Source & history <span>{sourceOpen ? '▾' : '▸'}</span>
                    </button>
                    {sourceOpen ? (
                        <div className={styles.disclosureBody}>
                            {(item.sources || []).length === 0 ? <div>Added in Lens. There is no imported source value.</div> : null}
                            {(item.sources || []).map(source => (
                                <div key={`${source.snapshot_id}-${source.worksheet}`}>
                                    {sourceLine(source)}
                                    {source.missing_from_latest ? ' · Missing from latest file' : ''}
                                </div>
                            ))}
                            {(item.revisions || []).map(revision => (
                                <div key={revision.version}>
                                    Version {revision.version} · {revision.change_source}
                                </div>
                            ))}
                        </div>
                    ) : null}
                    <button type="button" className={styles.disclosure} onClick={() => setCustomOpen(open => !open)}>
                        Custom fields <span>{customOpen ? '▾' : '▸'}</span>
                    </button>
                    {customOpen ? (
                        <div className={styles.disclosureBody}>
                            {customEntries.length === 0 ? <div>—</div> : null}
                            <div className={styles.customFieldList}>
                                {customEntries.map(entry => (
                                    <div key={entry.key} className={styles.customFieldRow}>
                                        <p className={styles.detailLabel}>{entry.label}</p>
                                        <p className={styles.customFieldValue}>{String(entry.value ?? '').trim() || '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </aside>
        </div>
    );
}
